import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, Paper, Alert, LinearProgress,
  MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, Radio, RadioGroup,
  FormControlLabel, FormControl, IconButton, Tooltip, Collapse
} from '@mui/material';
import {
  Campaign as CampaignIcon, ContentCopy as CopyIcon, Add as AddIcon, Refresh as RefreshIcon,
  Dns as DnsIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon,
  Edit as EditIcon, Delete as DeleteIcon
} from '@mui/icons-material';
import { ApiService } from '../services/api';

const API_BASE = ApiService.OUTREACH_API_BASE_URL;

interface MwList {
  uid: string;
  name: string;
  defaults?: { fromName: string | null; fromEmail: string | null; replyTo: string | null; subject: string | null };
}
interface MwTemplate { template_uid: string; name: string; }
interface MwCampaign {
  uid: string;
  name: string;
  subject: string;
  status: string;
  type: string;
  sendAt: string;
  listUid: string;
  fromName?: string;
  fromEmail?: string;
}
interface MwDeliveryServer {
  id: string;
  type: string;
  name: string;
  hostname: string;
  status: string;
  hourlyQuota?: number;
}

const emptyForm = {
  name: '', subject: '', fromName: '', fromEmail: '', replyTo: '',
  listUid: '', templateUid: '', sendMode: 'schedule' as 'now' | 'schedule', scheduledAt: '',
  deliveryServerUids: [] as string[]
};

const statusColor = (s: string): 'success' | 'warning' | 'info' | 'default' => {
  if (s === 'sent') return 'success';
  if (s === 'sending' || s === 'pending-sent') return 'warning';
  if (s === 'draft') return 'info';
  return 'default';
};

const MailwhizCampaignsPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<MwCampaign[]>([]);
  const [lists, setLists] = useState<MwList[]>([]);
  const [templates, setTemplates] = useState<MwTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [deliveryServers, setDeliveryServers] = useState<MwDeliveryServer[]>([]);
  const [deliveryServersOpen, setDeliveryServersOpen] = useState(false);
  const [deliveryServersLoaded, setDeliveryServersLoaded] = useState(false);

  const [isNewOpen, setIsNewOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [creating, setCreating] = useState(false);
  const [confirmSendNow, setConfirmSendNow] = useState(false);

  const [editingUid, setEditingUid] = useState<string | null>(null);
  const [loadingEdit, setLoadingEdit] = useState(false);

  const [copyTarget, setCopyTarget] = useState<MwCampaign | null>(null);
  const [copying, setCopying] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState<MwCampaign | null>(null);
  const [deleting, setDeleting] = useState(false);

  const token = () => localStorage.getItem('ems_access_token') || localStorage.getItem('token');
  const authFetch = (path: string, options: RequestInit = {}) => fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token() ? { Authorization: `Bearer ${token()}` } : {}),
      ...options.headers
    }
  });

  const loadCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await authFetch('/mailwhiz/campaigns');
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || data.error);
      setCampaigns(data.campaigns || []);
    } catch (err: any) {
      setError(`Could not load campaigns: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadListsAndTemplates = useCallback(async () => {
    try {
      const [listsRes, templatesRes] = await Promise.all([
        authFetch('/mailwhiz/lists'),
        authFetch('/mailwhiz/templates')
      ]);
      const listsData = await listsRes.json();
      if (listsRes.ok) setLists(listsData.lists || []);
      const templatesData = await templatesRes.json();
      if (templatesRes.ok) setTemplates(templatesData.data?.records || []);
    } catch (_) { /* surfaced lazily when the New Campaign dialog is used */ }
  }, []);

  const loadDeliveryServers = useCallback(async () => {
    try {
      setError(null);
      const res = await authFetch('/mailwhiz/delivery-servers');
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || data.error);
      setDeliveryServers(data.servers || []);
      setDeliveryServersLoaded(true);
    } catch (err: any) {
      setError(`Could not load delivery servers: ${err.message}`);
    }
  }, []);

  useEffect(() => {
    loadCampaigns();
    loadListsAndTemplates();
    loadDeliveryServers();
  }, [loadCampaigns, loadListsAndTemplates, loadDeliveryServers]);

  const toggleDeliveryServers = () => {
    const next = !deliveryServersOpen;
    setDeliveryServersOpen(next);
    if (next && !deliveryServersLoaded) loadDeliveryServers();
  };

  const setField = (key: keyof typeof emptyForm) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  // Senders used on past campaigns, so a repeat send doesn't require retyping
  // the from name/email - newest campaigns first, deduplicated by email.
  const knownSenders = campaigns
    .filter((c) => c.fromEmail)
    .reduce((acc: { fromName: string; fromEmail: string }[], c) => {
      if (!acc.some((s) => s.fromEmail === c.fromEmail)) {
        acc.push({ fromName: c.fromName || '', fromEmail: c.fromEmail! });
      }
      return acc;
    }, []);

  const applyKnownSender = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sender = knownSenders.find((s) => s.fromEmail === e.target.value);
    if (sender) setForm((f) => ({ ...f, fromName: sender.fromName, fromEmail: sender.fromEmail }));
  };

  // Each MailWhiz list carries its own sender defaults - fill the form from them as soon
  // as a list is picked, rather than leaving From name/email/reply-to empty for the user
  // to retype every time. Only fills fields the user hasn't already typed something into.
  const applyListSelection = (e: React.ChangeEvent<HTMLInputElement>) => {
    const listUid = e.target.value;
    const list = lists.find((l) => l.uid === listUid);
    setForm((f) => ({
      ...f,
      listUid,
      fromName: f.fromName || list?.defaults?.fromName || '',
      fromEmail: f.fromEmail || list?.defaults?.fromEmail || '',
      replyTo: f.replyTo || list?.defaults?.replyTo || '',
      subject: f.subject || list?.defaults?.subject || ''
    }));
  };

  const canSubmit = !!(form.name && form.subject && form.fromName && form.fromEmail && form.listUid && form.templateUid
    && (form.sendMode === 'now' || form.scheduledAt));

  const submitCampaign = async () => {
    const isEditing = !!editingUid;
    try {
      setCreating(true);
      setError(null);
      const res = await authFetch(isEditing ? `/mailwhiz/campaigns/${editingUid}` : '/mailwhiz/campaigns', {
        method: isEditing ? 'PUT' : 'POST',
        body: JSON.stringify({
          name: form.name,
          subject: form.subject,
          fromName: form.fromName,
          fromEmail: form.fromEmail,
          replyTo: form.replyTo,
          listUid: form.listUid,
          templateUid: form.templateUid,
          sendMode: form.sendMode,
          scheduledAt: form.scheduledAt ? form.scheduledAt.replace('T', ' ') + ':00' : undefined,
          deliveryServerUids: form.deliveryServerUids
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || data.error);
      if (isEditing) {
        setSuccess(form.sendMode === 'now'
          ? `"${form.name}" updated and queued to send now.`
          : `"${form.name}" updated and scheduled for ${data.sendAt}.`);
      } else {
        setSuccess(form.sendMode === 'now'
          ? `Campaign created and queued to send now (uid ${data.uid}).`
          : `Campaign created and scheduled for ${data.sendAt}.`);
      }
      setIsNewOpen(false);
      setConfirmSendNow(false);
      setEditingUid(null);
      setForm(emptyForm);
      loadCampaigns();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleCreateClick = () => {
    if (form.sendMode === 'now') {
      setConfirmSendNow(true);
    } else {
      submitCampaign();
    }
  };

  const openEditDialog = async (c: MwCampaign) => {
    try {
      setLoadingEdit(true);
      setError(null);
      setEditingUid(c.uid);
      const res = await authFetch(`/mailwhiz/campaigns/${c.uid}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || data.error);
      setForm({
        name: data.name || '',
        subject: data.subject || '',
        fromName: data.fromName || '',
        fromEmail: data.fromEmail || '',
        replyTo: data.replyTo || '',
        listUid: data.listUid || '',
        templateUid: data.templateUid || '',
        sendMode: 'schedule',
        scheduledAt: data.sendAt ? data.sendAt.replace(' ', 'T').slice(0, 16) : '',
        deliveryServerUids: data.deliveryServerUids || []
      });
      setIsNewOpen(true);
    } catch (err: any) {
      setEditingUid(null);
      setError(`Could not load campaign for editing: ${err.message}`);
    } finally {
      setLoadingEdit(false);
    }
  };

  const closeFormDialog = () => {
    setIsNewOpen(false);
    setEditingUid(null);
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      setError(null);
      const res = await authFetch(`/mailwhiz/campaigns/${deleteTarget.uid}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || data.error);
      setSuccess(`"${deleteTarget.name}" deleted.`);
      setDeleteTarget(null);
      loadCampaigns();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDeleting(false);
    }
  };

  const doCopy = async () => {
    if (!copyTarget) return;
    try {
      setCopying(true);
      setError(null);
      const res = await authFetch(`/mailwhiz/campaigns/${copyTarget.uid}/copy`, { method: 'POST' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || data.error);
      setSuccess(`"${copyTarget.name}" duplicated as a new draft (uid ${data.uid}). Edit and send it from MailWhiz, or build a new one here.`);
      setCopyTarget(null);
      loadCampaigns();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCopying(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <CampaignIcon sx={{ color: '#3b82f6' }} /> Email Campaigns
      </Typography>
      <Typography variant="body2" sx={{ color: '#475569', mb: 3 }}>
        Create and send MailWhiz campaigns, or duplicate an existing one as a starting point.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1.5 }}>
        <IconButton size="small" onClick={loadCampaigns}><RefreshIcon fontSize="small" /></IconButton>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => { setForm(emptyForm); setIsNewOpen(true); }}
          sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
        >
          New Campaign
        </Button>
      </Box>

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Send at</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campaigns.length === 0 && !loading && (
              <TableRow><TableCell colSpan={5} align="center" sx={{ color: '#94a3b8', py: 3 }}>
                No campaigns yet, or the API key doesn't have Campaigns permission enabled in MailWhiz.
              </TableCell></TableRow>
            )}
            {campaigns.map((c) => (
              <TableRow key={c.uid} hover>
                <TableCell sx={{ fontWeight: 600 }}>{c.name}</TableCell>
                <TableCell sx={{ color: '#475569' }}>{c.subject}</TableCell>
                <TableCell><Chip size="small" label={c.status} color={statusColor(c.status)} /></TableCell>
                <TableCell sx={{ color: '#64748b', fontSize: '0.78rem' }}>{c.sendAt}</TableCell>
                <TableCell align="right">
                  {c.status === 'draft' && (
                    <Tooltip title="Edit, confirm & schedule, or send now">
                      <IconButton size="small" onClick={() => openEditDialog(c)} disabled={loadingEdit}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Duplicate as a new draft">
                    <IconButton size="small" onClick={() => setCopyTarget(c)}>
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {c.status === 'draft' && (
                    <Tooltip title="Delete this draft">
                      <IconButton size="small" onClick={() => setDeleteTarget(c)}>
                        <DeleteIcon fontSize="small" sx={{ color: '#b91c1c' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* New / edit campaign dialog */}
      <Dialog open={isNewOpen} onClose={() => !creating && closeFormDialog()} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>{editingUid ? 'Edit Draft Campaign' : 'New Campaign'}</DialogTitle>
        <DialogContent sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 1 }}>
          <TextField label="Campaign name *" value={form.name} onChange={setField('name')} sx={{ gridColumn: '1 / -1' }} />
          <TextField label="Subject *" value={form.subject} onChange={setField('subject')} sx={{ gridColumn: '1 / -1' }} />
          {knownSenders.length > 0 && (
            <TextField select label="Use a previous sender" value="" onChange={applyKnownSender}
              helperText="Fills in the From name/email below" sx={{ gridColumn: '1 / -1' }}>
              {knownSenders.map((s) => (
                <MenuItem key={s.fromEmail} value={s.fromEmail}>{s.fromName} &lt;{s.fromEmail}&gt;</MenuItem>
              ))}
            </TextField>
          )}
          <TextField label="From name *" value={form.fromName} onChange={setField('fromName')} />
          <TextField label="From email *" value={form.fromEmail} onChange={setField('fromEmail')} />
          <TextField label="Reply-to email" helperText="Defaults to from email" value={form.replyTo} onChange={setField('replyTo')} />
          <TextField select label="List *" value={form.listUid} onChange={applyListSelection}
            helperText="Also fills From name/email/reply-to from this list's own defaults">
            {lists.map((l) => <MenuItem key={l.uid} value={l.uid}>{l.name}</MenuItem>)}
          </TextField>
          <TextField select label="Template *" value={form.templateUid} onChange={setField('templateUid')} sx={{ gridColumn: '1 / -1' }}
            helperText={templates.length === 0 ? "No templates loaded — check the API key's Templates permission" : ' '}>
            {templates.map((t) => <MenuItem key={t.template_uid} value={t.template_uid}>{t.name}</MenuItem>)}
          </TextField>
          <TextField
            select
            label="Delivery server(s)"
            sx={{ gridColumn: '1 / -1' }}
            value={form.deliveryServerUids}
            onChange={(e: any) => setForm((f) => ({ ...f, deliveryServerUids: e.target.value as string[] }))}
            SelectProps={{
              multiple: true,
              renderValue: (selected: any) => (selected as string[])
                .map((id) => deliveryServers.find((s) => s.id === id)?.name || id)
                .join(', ')
            }}
            helperText={deliveryServers.length === 0
              ? 'No delivery servers loaded'
              : 'Optional — leave blank to let MailWhiz auto-assign. Only takes effect if "select delivery servers for campaigns" is enabled on this account. Per-day sending limits live on the server itself (set in the MailWhiz admin panel), not here.'}
          >
            {deliveryServers.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name} ({s.hostname}){s.hourlyQuota ? ` — ${s.hourlyQuota}/hr` : ''}
              </MenuItem>
            ))}
          </TextField>

          <FormControl sx={{ gridColumn: '1 / -1' }}>
            <RadioGroup row value={form.sendMode} onChange={(e) => setForm((f) => ({ ...f, sendMode: e.target.value as any }))}>
              <FormControlLabel value="schedule" control={<Radio />} label="Schedule for later" />
              <FormControlLabel value="now" control={<Radio />} label="Send now" />
            </RadioGroup>
          </FormControl>
          {form.sendMode === 'schedule' && (
            <TextField
              type="datetime-local"
              label="Send at"
              InputLabelProps={{ shrink: true }}
              value={form.scheduledAt}
              onChange={setField('scheduledAt')}
              sx={{ gridColumn: '1 / -1' }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={closeFormDialog} disabled={creating} sx={{ textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" disabled={!canSubmit || creating} onClick={handleCreateClick} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 3 }}>
            {creating
              ? (editingUid ? 'Saving...' : 'Creating...')
              : form.sendMode === 'now'
                ? (editingUid ? 'Save & Send' : 'Create & Send')
                : (editingUid ? 'Save & Schedule' : 'Create & Schedule')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm send-now */}
      <Dialog open={confirmSendNow} onClose={() => setConfirmSendNow(false)}>
        <DialogTitle sx={{ fontWeight: 700, color: '#b91c1c' }}>Send to real recipients now?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {editingUid
              ? `This saves "${form.name}" and queues it to send immediately to everyone on the selected list. This cannot be undone once MailWhiz starts delivering it.`
              : `This creates "${form.name}" and queues it to send immediately to everyone on the selected list. This cannot be undone once MailWhiz starts delivering it.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmSendNow(false)} sx={{ textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={submitCampaign} disabled={creating} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
            {creating ? 'Sending...' : 'Yes, send now'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm delete */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle sx={{ fontWeight: 700, color: '#b91c1c' }}>Delete this draft?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Permanently deletes "{deleteTarget?.name}" from MailWhiz. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting} sx={{ textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={doDelete} disabled={deleting} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm copy */}
      <Dialog open={!!copyTarget} onClose={() => setCopyTarget(null)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Duplicate campaign?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Creates a new draft copy of "{copyTarget?.name}". The copy is never auto-sent — edit and send it separately.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCopyTarget(null)} sx={{ textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={doCopy} disabled={copying} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}>
            {copying ? 'Copying...' : 'Copy'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delivery servers - read only; registering a new one is MailWhiz admin panel only */}
      <Paper variant="outlined" sx={{ borderRadius: '12px', mt: 3 }}>
        <Box
          onClick={toggleDeliveryServers}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 2, cursor: 'pointer' }}
        >
          <DnsIcon sx={{ color: '#64748b', fontSize: 20 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', flex: 1 }}>
            Delivery servers {deliveryServersLoaded && `(${deliveryServers.length})`}
          </Typography>
          <Typography variant="caption" sx={{ color: '#94a3b8' }}>Read-only — add new ones in the MailWhiz admin panel</Typography>
          {deliveryServersOpen ? <ExpandLessIcon sx={{ color: '#64748b' }} /> : <ExpandMoreIcon sx={{ color: '#64748b' }} />}
        </Box>
        <Collapse in={deliveryServersOpen}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Hostname</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {deliveryServers.map((s) => (
                <TableRow key={s.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{s.name}</TableCell>
                  <TableCell sx={{ color: '#64748b' }}>{s.hostname}</TableCell>
                  <TableCell>{s.type}</TableCell>
                  <TableCell><Chip size="small" label={s.status} color={s.status === 'active' ? 'success' : 'default'} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Collapse>
      </Paper>
    </Box>
  );
};

export default MailwhizCampaignsPage;
